/**
 * paylater-checkout-router.js  (v3 — single button + payment-choice popup)
 *
 * On a PayLater-eligible product, clicking the ONE "Add to Cart" button opens a
 * popup asking how to pay, then redirects:
 *   • "Pay in 4 — PayLater"  → custom checkout (/pages/custom-checkout), intent flagged
 *   • "Pay full amount"      → Shopify NATIVE checkout (/checkout), full price
 *   • "Just add to cart"     → adds the item and closes (keep shopping)
 *
 * Non-PayLater products keep their normal add-to-cart behavior. The generic cart
 * "Checkout" button routes to custom only when the PayLater intent is set,
 * otherwise Shopify native — even if the cart contains PayLater items.
 */
(function () {
  'use strict';
  var BNPL_CHECK = 'https://admin.menahub.com/api/bnpl_check.php';
  var NATIVE = '/checkout';

  // Handle of the product this PDP is showing, captured in initPdp() — the
  // PayLater checkout is addressed by handle, not variant id.
  var PDP_HANDLE = '';

  function localePrefix() {
    return location.pathname.indexOf('/ar') === 0 ? '/ar' : '';
  }

  // "Pay in 4" used to land on /pages/paylater — the EMI MARKETING page, which
  // just lists PayLater products. It was pointed there as a workaround for
  // /pages/custom-checkout showing an empty cart, but that was an old checkout
  // build that never called loadQuickProduct(); the current one reads ?quick=
  // and builds the one-item cart itself. So the workaround now costs a sale:
  // the shopper picks "Pay in 4" and is dropped on a product listing instead of
  // a checkout.
  //
  // Address the item explicitly (quick/qty/v) rather than relying on the cart,
  // which is what made the old flow arrive empty. ?v= carries the exact variant
  // the shopper selected.
  // Cart-driven PayLater checkout: no ?quick=, so the checkout loads the
  // shopper's actual cart rather than a synthetic one-item one.
  function cartPlUrl() {
    return localePrefix() + '/pages/custom-checkout?intent=paylater&src=cart';
  }

  function plUrl(handle, qty, variantId) {
    var h = handle || PDP_HANDLE;
    if (!h) {
      // No handle to check out with — the marketing page is still a better
      // landing than a checkout that would open empty.
      return localePrefix() + '/pages/paylater';
    }
    return localePrefix() + '/pages/custom-checkout'
      + '?quick=' + encodeURIComponent(h)
      + '&qty=' + (qty || 1)
      + (variantId ? '&v=' + encodeURIComponent(variantId) : '')
      + '&intent=paylater&src=pdp';
  }
  // checkout-router.js rewrites every a[href="/checkout"] to the configured
  // checkout path (/pages/custom-checkout) and publishes it on
  // window.__mhCheckoutPath. Once it has run, an href-only selector stops
  // matching the cart drawer's button, so this router never fired and the
  // PayLater intent was never added — the shopper reached a full-price
  // checkout. Whether that rewrite lands before or after the shopper clicks is
  // a race, which is why it failed only sometimes. Match the rewritten form and
  // the drawer's own class too.
  var CHECKOUT_SEL = 'a[href="/checkout"], a[href^="/checkout?"], '
    + 'a[href="/pages/custom-checkout"], a[href^="/pages/custom-checkout?"], '
    + 'a[href="/ar/pages/custom-checkout"], a[href^="/ar/pages/custom-checkout?"], '
    + '.mh-cart-checkout-btn, .btn-checkout, button[name="checkout"], '
    + 'input[name="checkout"], [data-mh-checkout]';

  /* Where a NON-PayLater cart belongs. This store routes ordinary orders
     through the custom checkout (checkout-router.js decides and publishes the
     path), so hardcoding Shopify native here would drag every normal cart out
     of the configured checkout. */
  function normalCheckoutUrl() {
    return window.__mhCheckoutPath || NATIVE;
  }
  var ADD_SEL = '[data-add-cart], [data-mobile-add-cart], .mh-btn-add-cart, [data-buy-now], [data-mobile-buy-now]';
  var deciding = false;

  function go(url) { window.location.href = url; }

  function setIntent(val) {
    return fetch('/cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { checkout_intent: val } }), credentials: 'same-origin'
    });
  }
  function addItem(vid, qty, props) {
    if (!vid) return Promise.resolve();
    var body = { id: Number(vid), quantity: qty || 1 };
    if (props) body.properties = props;
    return fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body), credentials: 'same-origin'
    });
  }
  // A cart line counts as PayLater when it carries the hidden `_paylater`
  // property we stamp when "Pay in 4" is chosen. Underscore-prefixed so it stays
  // out of the native checkout display but is still readable in Liquid + cart.js.
  function lineIsPaylater(it) {
    return !!(it && it.properties && it.properties._paylater);
  }
  function cartHasPaylater(cart) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) { if (lineIsPaylater(items[i])) return true; }
    return false;
  }

  /* Does this cart belong in the PayLater checkout?
     The `_paylater` property only marks what the shopper explicitly CHOSE, so
     adding an eligible product with "Just add to cart" and then hitting
     Checkout sent them to the native checkout at full price — even though the
     drawer had just shown them the instalment. Eligibility is the real
     question, so ask the API about what is actually in the cart.
     A cart with no eligible line NEVER goes near the PayLater checkout. */
  function cartWantsPaylater(cart) {
    var items = (cart && cart.items) || [];
    if (!items.length) return Promise.resolve(false);
    // An explicit choice is authoritative and needs no round trip.
    if (cartHasPaylater(cart)) return Promise.resolve(true);

    var ids = [];
    for (var i = 0; i < items.length; i++) {
      var pid = items[i] && items[i].product_id;
      if (pid && ids.indexOf(pid) === -1) ids.push(pid);
    }
    if (!ids.length) return Promise.resolve(false);

    return fetch(BNPL_CHECK + '?shopify_product_ids=' + ids.join(','))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || !d.bnpl_enabled || !d.products) return false;
        for (var j = 0; j < d.products.length; j++) {
          if (d.products[j] && d.products[j].eligible) return true;
        }
        return false;
      })
      // If the check cannot be reached, fall back to the explicit choice only.
      // Sending a shopper to the native checkout is recoverable; sending a
      // non-PayLater cart into the PayLater flow is not.
      .catch(function () { return cartHasPaylater(cart); });
  }

  // ── Generic cart "Checkout" → PayLater checkout for PayLater carts ─────────
  function decideAndGo() {
    if (deciding) return;
    deciding = true;
    // Route by the ACTUAL cart contents, not the cart-level intent flag, so a
    // stale flag cannot hijack it. Mixed carts (some PayLater, some full-price)
    // go to the PayLater checkout, whose split-order flow separates them.
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        return cartWantsPaylater(cart).then(function (wants) {
          // A cart cannot be addressed by ?quick= (that is one product), so the
          // PayLater checkout reads the real cart — which it does whenever
          // ?quick= is absent, redirecting to /cart if it is empty.
          go(wants ? cartPlUrl() : normalCheckoutUrl());
        });
      })
      .catch(function () { go(normalCheckoutUrl()); });
  }
  document.addEventListener('click', function (e) {
    // The selector now matches links to the custom checkout itself, so leave
    // that page's own controls alone — we are already where this would send.
    if (location.pathname.indexOf('/pages/custom-checkout') !== -1) return;
    var t = e.target.closest ? e.target.closest(CHECKOUT_SEL) : null;
    if (!t) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    decideAndGo();
  }, true);

  // ── PDP "Buy on PayLater" button → custom checkout (quick mode, intent flagged) ──
  // Delegated so it survives async-rendered PDPs and needs no inline JS (an inline
  // onclick broke because {{ product.handle | json }} emitted quotes that closed the
  // attribute). Reads the handle from data-handle and the variant from the product form.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('.mh-pl__buy') : null;
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;
    var handle = btn.getAttribute('data-handle') || '';
    if (!handle) return;
    btn.disabled = true;
    // Try each source IN PRIORITY ORDER. A single comma-separated
    // querySelector does NOT do that — it returns the first match in DOCUMENT
    // ORDER across the whole list, so a related-products / recommendation
    // widget that renders its own form[action^="/cart/add"] ABOVE the main
    // product form won that race and we sent ANOTHER product's variant id.
    // Observed live on mim-ss-water-cooler-2-tap-25-gal-cnr25t2: the button
    // emitted v=49952450805993, a variant of the 3-Tap 45 Gal product, while
    // this product's only variant is 49952450838761. Checkout then can't find
    // that variant on the handle and silently falls back to the first one —
    // harmless on a single-variant product, but on a multi-variant product it
    // quietly ignores the variant the shopper actually chose.
    var vEl = document.querySelector('.mh-variant-id-input')
           || document.querySelector('#mh-product-form [name="id"]')
           || document.querySelector('form[action^="/cart/add"] [name="id"]');
    var v   = vEl && vEl.value;
    var pre = location.pathname.indexOf('/ar') === 0 ? '/ar' : '';
    go(pre + '/pages/custom-checkout?quick=' + encodeURIComponent(handle)
       + (v ? '&v=' + encodeURIComponent(v) : '')
       + '&qty=1&intent=paylater&src=pdp');
  }, false);

  // PayLater entry used by the popup: flag intent → add → custom checkout.
  window.mhStartPaylater = function (variantId, qty, handle) {
    var dest = plUrl(handle, qty, variantId);
    // Still stamp the cart: the line keeps its `_paylater` property so the
    // generic cart Checkout button routes correctly if the shopper backs out
    // of the PayLater checkout and leaves the item sitting in the cart.
    setIntent('paylater')
      .then(function () { return addItem(variantId, qty, { _paylater: 'Pay in 4' }); })
      .then(function () { go(dest); })
      .catch(function () { go(dest); });
  };

  // ── Payment-choice popup (PayLater products only) ──────────────────────────
  function injectCss() {
    if (document.getElementById('mh-payc-css')) return;
    var s = document.createElement('style');
    s.id = 'mh-payc-css';
    s.textContent =
      '.mh-payc-ov{position:fixed;inset:0;z-index:2147483600;background:rgba(15,23,42,.55);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;padding:18px;animation:mhPaycF .15s ease}' +
      '@keyframes mhPaycF{from{opacity:0}to{opacity:1}}' +
      '.mh-payc-card{background:#fff;border-radius:20px;max-width:420px;width:100%;padding:22px 20px 18px;box-shadow:0 24px 60px -20px rgba(15,23,42,.5);position:relative;animation:mhPaycS .18s cubic-bezier(.16,1,.3,1)}' +
      '@keyframes mhPaycS{from{transform:translateY(14px);opacity:0}to{transform:none;opacity:1}}' +
      '.mh-payc-x{position:absolute;top:10px;right:12px;border:0;background:none;font-size:26px;line-height:1;color:#94a3b8;cursor:pointer}' +
      '.mh-payc-h{font-size:18px;font-weight:800;color:#0f172a;margin:0 0 2px}' +
      '.mh-payc-sub{font-size:13px;color:#64748b;margin:0 0 16px}' +
      '.mh-payc-btn{display:flex;flex-direction:column;align-items:flex-start;gap:2px;width:100%;text-align:left;border:0;border-radius:14px;padding:13px 16px;margin-bottom:10px;cursor:pointer;transition:filter .15s,transform .1s}' +
      '.mh-payc-btn:hover{filter:brightness(1.04)}.mh-payc-btn:active{transform:scale(.99)}' +
      '.mh-payc-btn--pl{background:linear-gradient(135deg,#6938d6,#9333ea);color:#fff;box-shadow:0 8px 20px -8px rgba(105,56,214,.6)}' +
      '.mh-payc-btn--full{background:#0f172a;color:#fff}' +
      '.mh-payc-bt{font-size:15px;font-weight:800;line-height:1.1}' +
      '.mh-payc-bs{font-size:11.5px;font-weight:600;opacity:.85}' +
      '.mh-payc-add{display:block;width:100%;text-align:center;border:0;background:none;color:#6938d6;font-size:13px;font-weight:700;cursor:pointer;padding:8px 0 2px}' +
      '.mh-payc-add:hover{text-decoration:underline}' +
      '.mh-payc-btn[disabled],.mh-payc-add[disabled]{opacity:.6;cursor:default}';
    document.head.appendChild(s);
  }
  function closeModal() {
    var ov = document.getElementById('mh-payc-ov');
    if (ov) ov.parentNode.removeChild(ov);
    document.documentElement.style.overflow = '';
  }
  function bigBtn(cls, title, subtitle, onClick) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'mh-payc-btn ' + cls;
    var t = document.createElement('span'); t.className = 'mh-payc-bt'; t.textContent = title;
    var s = document.createElement('span'); s.className = 'mh-payc-bs'; s.textContent = subtitle;
    b.appendChild(t); b.appendChild(s);
    b.addEventListener('click', function () { b.disabled = true; onClick(); });
    return b;
  }
  function openChoiceModal() {
    injectCss();
    var form = document.getElementById('mh-product-form');
    var idEl = form ? form.querySelector('[name="id"]') : null;
    var qtyEl = form ? form.querySelector('[name="quantity"]') : null;
    var vid = idEl ? idEl.value : null;
    var qty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;

    closeModal();
    var ov = document.createElement('div'); ov.id = 'mh-payc-ov'; ov.className = 'mh-payc-ov';
    var card = document.createElement('div'); card.className = 'mh-payc-card';

    var x = document.createElement('button'); x.type = 'button'; x.className = 'mh-payc-x'; x.textContent = '×';
    x.addEventListener('click', closeModal);

    var h = document.createElement('div'); h.className = 'mh-payc-h'; h.textContent = 'How would you like to pay?';
    var sub = document.createElement('p'); sub.className = 'mh-payc-sub'; sub.textContent = 'Choose an option to continue to checkout.';

    var plBtn = bigBtn('mh-payc-btn--pl', 'Pay in 4 — 0% interest', 'Split into 4 monthly payments · PayLater', function () {
      window.mhStartPaylater(vid, qty, PDP_HANDLE);
    });
    var fullBtn = bigBtn('mh-payc-btn--full', 'Pay full amount', 'Card / Cash on Delivery · normal checkout', function () {
      setIntent('').then(function () { return addItem(vid, qty); }).then(function () { go(NATIVE); }).catch(function () { go(NATIVE); });
    });
    var addLink = document.createElement('button');
    addLink.type = 'button'; addLink.className = 'mh-payc-add'; addLink.textContent = 'Just add to cart & keep shopping';
    addLink.addEventListener('click', function () {
      addLink.disabled = true;
      setIntent('').then(function () { return addItem(vid, qty); }).then(function () {
        closeModal();
        try { document.dispatchEvent(new CustomEvent('cart:refresh')); } catch (e) {}
        location.reload();
      }).catch(closeModal);
    });

    card.appendChild(x); card.appendChild(h); card.appendChild(sub);
    card.appendChild(plBtn); card.appendChild(fullBtn); card.appendChild(addLink);
    ov.appendChild(card);
    ov.addEventListener('click', function (e) { if (e.target === ov) closeModal(); });
    document.body.appendChild(ov);
    document.documentElement.style.overflow = 'hidden';
  }

  // Intercept Add-to-Cart on eligible products → open the choice popup instead.
  var pdpBound = false;
  function bindPdp() {
    if (pdpBound) return;
    pdpBound = true;
    document.addEventListener('submit', function (e) {
      if (!e.target.closest || !e.target.closest('form[action*="/cart/add"]')) return;
      e.preventDefault(); e.stopImmediatePropagation(); openChoiceModal();
    }, true);
    document.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest(ADD_SEL) : null;
      if (!b || b.disabled) return;
      if (e.target.closest('#mh-payc-ov')) return; // ignore clicks inside our popup
      e.preventDefault(); e.stopImmediatePropagation(); openChoiceModal();
    }, true);
  }
  function initPdp() {
    // Product page in either locale: /products/… or /ar/products/…
    if (location.pathname.indexOf('/products/') === -1) return;
    fetch(location.pathname + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        if (!p || !p.id) return;
        PDP_HANDLE = p.handle || '';
        fetch(BNPL_CHECK + '?shopify_product_ids=' + p.id)
          .then(function (r) { return r.json(); })
          .then(function (d) {
            if (d && d.products && d.products[0] && d.products[0].eligible) bindPdp();
          })
          .catch(function () {});
      })
      .catch(function () {});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initPdp);
  else initPdp();
})();
